import type {
    ActorEvent,
    AdvancedTableViewViewConfig,
    FieldADTValue,
    RecordLikeProtocol,
    RichTextContentProtocol,
    SelectedMode,
    TableColumns,
    TableColumnWidth,
    ViewField
} from '@lighthouse/core'
import type { FetchPexelsParams, PexelsResponse, UseUploadFileSParameter } from '@lighthouse/shared'
import { getViewColumns, ViewEmptyState } from '@lighthouse/shared'
import type { UploadyProps } from '@rpldy/uploady'
import { find } from 'rambda'
import React, { memo, useCallback, useMemo, useRef } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { Table } from './AdvancedTable'
import * as SC from './styles'

interface AdvancedTableBlockProps {
    tablePropsCache?: TableColumns
    selectedRecords: string[]
    blockWidth?: number
    tableColumnCache?: TableColumnWidth
    uploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    richTextUploadOptions: UploadyProps
    videoUploadOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    onCellChange: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onCellUpdate: (recordId: string, fieldValue: FieldADTValue) => Promise<boolean>
    onSelectedRecords: (recordIds: string[]) => void
    onRecordOperatorActionTrigger: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    onSelectModeChange: (mode?: SelectedMode) => void
    onTableColumnWidthChange: (val: TableColumnWidth) => void
    onFetchPexelsList: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

/**
 *
 * @param param
 * @returns
 * blockData: {
 *   fields: { id: string; type: string; name: string }[]
 *   records: { id: string; content: Record<number, string> }[]
 * }
 */

const AdvancedTableBlock: React.FC<AdvancedTableBlockProps> = ({
    tablePropsCache,
    selectedRecords,
    blockWidth,
    tableColumnCache,
    uploadOptions,
    richTextUploadOptions,
    videoUploadOptions,
    onCellChange,
    onCellUpdate,
    onSelectedRecords,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderRichText,
    onSelectModeChange,
    onTableColumnWidthChange,
    onFetchPexelsList
}) => {
    const { blockData, dataSource, recordPoints } = useViewBlockContext()
    const tableBlockRef = useRef<HTMLDivElement>(null)
    const { schema, appId: dsAppId, viewOptions } = dataSource
    const { tableProps } = viewOptions
    const { id: blockId, title = '无标题', config } = blockData
    const {
        anchor,
        canDelete = false,
        canExport,
        canPrint,
        canDisplay,
        pointer,
        viewFieldSettings,
        highLightRules,
        eventList,
        breakPoint: baseBreakPointConfig
    } = config as AdvancedTableViewViewConfig
    const { emptyView } = baseBreakPointConfig || {}
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const tableRecordCheckable = !!canDelete || !!canExport || !!canPrint
    const noData = !pointer
    const noRecords = recordPoints && recordPoints.length === 0
    // const tableColumn = ViewSettingCache.getTableColumnWidthCache(blockId)

    // block 设置后的tableProps
    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId,
                tableProps,
                value: viewFieldSettings,
                schema,
                tableColumn: tableColumnCache
            }),
        [blockId, schema, tableColumnCache, tableProps, viewFieldSettings]
    )

    // 用户字段设置后的tableProps
    const [columns, userColumns] = useMemo(() => {
        if (!tablePropsCache || tablePropsCache.length === 0 || !canDisplay) {
            const list = settingColumns.filter(item => {
                return item.visible
            })
            return [list, list]
        }
        // const filterColumns = settingColumns.filter(item => item.visible)
        const columnsCache = tablePropsCache.reduce<ViewField[]>((prev, cur) => {
            const field = find(({ fieldId }) => fieldId === cur.id, settingColumns)
            if (field) {
                prev.push({
                    ...field,
                    visible: cur.visible
                })
            }
            return prev
        }, [])

        const columns = settingColumns.reduce<ViewField[]>((prev, cur) => {
            if (!cur.visible) {
                return prev
            }
            const field = find(({ fieldId }) => fieldId === cur.fieldId, prev)
            if (!field) {
                prev.push({
                    ...cur,
                    visible: false
                })
            }
            return prev
        }, columnsCache)

        const userColumns = columns.filter(item => item.visible)
        return [columns, userColumns]
    }, [tablePropsCache, canDisplay, settingColumns])

    const handleSelect = useCallback(
        (recordIds: string[]) => {
            onSelectedRecords(recordIds)
        },
        [onSelectedRecords]
    )

    return useMemo(() => {
        if (noData || !dataSource) {
            return (
                <SC.TableNoDataContent id={effectiveAnchorId}>
                    <ViewEmptyState config={emptyView} onRenderRichText={onRenderRichText} />
                </SC.TableNoDataContent>
            )
        }
        return (
            <SC.TableScrollerContent
                offsetScrollbars
                scrollbarSize={6}
                id={effectiveAnchorId}
                styles={{
                    root: {
                        overflowY: 'visible',
                        overflowX: 'hidden'
                    }
                }}
                data-content-type="Table"
                viewportRef={tableBlockRef}
            >
                <Table
                    checkable={tableRecordCheckable}
                    highLightRules={highLightRules}
                    scrollRef={tableBlockRef}
                    viewFields={userColumns}
                    blockWidth={blockWidth}
                    selectedRecords={selectedRecords}
                    uploadOptions={uploadOptions}
                    richTextUploadOptions={richTextUploadOptions}
                    videoUploadOptions={videoUploadOptions}
                    onRecordSelect={handleSelect}
                    onCellChange={onCellChange}
                    onCellUpdate={onCellUpdate}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderRichText}
                    onSelectModeChange={onSelectModeChange}
                    onTableColumnWidthChange={onTableColumnWidthChange}
                    onFetchPexelsList={onFetchPexelsList}
                />
                {noRecords && (
                    <SC.TableNoDataContent width={blockWidth}>
                        <ViewEmptyState config={emptyView} onRenderRichText={onRenderRichText} />
                    </SC.TableNoDataContent>
                )}
            </SC.TableScrollerContent>
        )
    }, [
        noData,
        dataSource,
        effectiveAnchorId,
        tableRecordCheckable,
        highLightRules,
        userColumns,
        blockWidth,
        selectedRecords,
        uploadOptions,
        richTextUploadOptions,
        videoUploadOptions,
        handleSelect,
        onCellChange,
        onCellUpdate,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderRichText,
        onSelectModeChange,
        onTableColumnWidthChange,
        onFetchPexelsList,
        noRecords,
        emptyView
    ])
}

export default memo(AdvancedTableBlock)
